import {
  Button,
  ControlGroup,
  FormGroup,
  NonIdealState,
  NumericInput,
  Spinner,
  Switch,
  Tab,
  type TabId,
  Tabs,
} from "@blueprintjs/core";
import { ItemMultiSelect, ItemSelect } from "@package/ui-apollo-components";
import {
  GetLayoutListNameIdDocument,
  GetPostListNameIdDocument,
  GetTagListNameIdDocument,
  type ProfileLayoutModel,
  type ProfileModel,
  ScreenType,
  Templates,
  useGetLayoutQuery,
} from "@package/ui-gql/index-data-access";
import { produce, type WritableDraft } from "immer";
import { uniq } from "lodash";
import { useMemo, useState } from "react";
import { LayoutCreateDrawer } from "../layout/LayoutEditDrawer";
import { PostCreateDrawer } from "../post/PostEditDrawer";
import { TagCreateDrawer } from "../tag/TagEditDrawer";

type ProfileLayoutPostTagSingleProps = {
  disabled?: boolean;
  layout?: ProfileLayoutModel | null;
  onLayoutChanged: (layout: ProfileLayoutModel) => void;
};

function ProfileLayoutPostTagSingle({
  disabled,
  layout,
  onLayoutChanged,
}: ProfileLayoutPostTagSingleProps) {
  const posts = useMemo<string[]>(
    () => (layout?.item?.posts?.filter((p) => !!p) ?? []) as string[],
    [layout?.item?.posts],
  );
  const tags = useMemo<string[]>(
    () => (layout?.item?.tags?.filter((p) => !!p) ?? []) as string[],
    [layout?.item?.tags],
  );

  return (
    <>
      <FormGroup
        fill
        label="Content"
        disabled={disabled}
        helperText="Content the selected Layout will automatically place"
      >
        <ItemMultiSelect
          activeItemIds={posts}
          createDrawerElement={PostCreateDrawer}
          disabled={disabled}
          document={GetPostListNameIdDocument}
          onItemRemoved={(id) =>
            onLayoutChanged({
              ...layout,
              item: {
                ...layout?.item,
                posts:
                  layout?.item?.posts?.filter((postId) => postId !== id) ?? [],
              },
            })
          }
          onItemSelected={(id) => {
            if (id) {
              onLayoutChanged({
                ...layout,
                item: {
                  ...layout?.item,
                  posts: uniq([...(layout?.item?.posts ?? []), id]),
                },
              });
            }
          }}
          sorted
        />
      </FormGroup>
      <FormGroup
        fill
        label="Tags (All Content with Tags)"
        disabled={disabled}
        helperText="Content the selected Layout will automatically place which are assigned the selected Tags"
      >
        <ItemMultiSelect
          activeItemIds={tags}
          createDrawerElement={TagCreateDrawer}
          disabled={disabled}
          document={GetTagListNameIdDocument}
          onItemRemoved={(id) =>
            onLayoutChanged({
              ...layout,
              item: {
                ...layout?.item,
                tags: layout?.item?.tags?.filter((tagId) => tagId !== id) ?? [],
              },
            })
          }
          onItemSelected={(id) => {
            if (id) {
              onLayoutChanged({
                ...layout,
                item: {
                  ...layout?.item,
                  tags: uniq([...(layout?.item?.tags ?? []), id]),
                },
              });
            }
          }}
          sorted
        />
      </FormGroup>
    </>
  );
}

type ProfileLayoutPostTagMultiProps = {
  disabled?: boolean;
  layout?: ProfileLayoutModel | null;
  onLayoutChanged: (layout: ProfileLayoutModel) => void;
  screens?: number;
};

function ProfileLayoutPostTagMulti({
  disabled,
  layout,
  onLayoutChanged,
}: ProfileLayoutPostTagMultiProps) {
  const { data, error, loading } = useGetLayoutQuery({
    variables: {
      id: layout?.layout ?? "",
    },
    skip: !layout?.layout,
  });
  const total =
    (data?.item?.template === Templates.Custom
      ? data?.item?.custom?.positions?.length
      : data?.item?.tileCount) ?? 1;

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <NonIdealState title="Failed Loading Layout" icon="warning-sign" />;
  }

  return (
    <>
      {Array.from({ length: total }).map((_, index) => (
        <FormGroup
          key={index}
          fill
          label={`Layout Position ${index + 1}`}
          disabled={disabled}
        >
          <ControlGroup>
            <FormGroup fill label="Content" disabled={disabled}>
              <ItemMultiSelect
                activeItemIds={
                  (layout?.items?.[index]?.posts as string[]) ?? []
                }
                createDrawerElement={PostCreateDrawer}
                disabled={disabled}
                document={GetPostListNameIdDocument}
                onItemRemoved={(id) => {
                  if (layout) {
                    onLayoutChanged(
                      produce(layout, (draft) => {
                        draft.items = draft.items ?? [];
                        draft.items[index] = draft.items[index] ?? {};
                        draft.items[index].posts =
                          draft.items[index].posts ?? [];
                        draft.items[index].posts = draft.items[
                          index
                        ].posts.filter((p) => p !== id);
                      }),
                    );
                  }
                }}
                onItemSelected={(id) => {
                  if (id && layout) {
                    onLayoutChanged(
                      produce(layout, (draft) => {
                        draft.items = draft.items ?? [];
                        draft.items[index] = draft.items[index] ?? {};
                        draft.items[index].posts =
                          draft.items[index].posts ?? [];
                        draft.items[index].posts = uniq([
                          ...(draft.items[index].posts ?? []),
                          id,
                        ]);
                      }),
                    );
                  }
                }}
                sorted
              />
            </FormGroup>
            <FormGroup fill label="Tags" disabled={disabled}>
              <ItemMultiSelect
                activeItemIds={(layout?.items?.[index]?.tags as string[]) ?? []}
                createDrawerElement={TagCreateDrawer}
                disabled={disabled}
                document={GetTagListNameIdDocument}
                onItemRemoved={(id) => {
                  if (layout) {
                    onLayoutChanged(
                      produce(layout, (draft) => {
                        draft.items = draft.items ?? [];
                        draft.items[index] = draft.items[index] ?? {};
                        draft.items[index].tags = draft.items[index].tags ?? [];
                        draft.items[index].tags = draft.items[
                          index
                        ].tags.filter((p) => p !== id);
                      }),
                    );
                  }
                }}
                onItemSelected={(id) => {
                  if (id && layout) {
                    onLayoutChanged(
                      produce(layout, (draft) => {
                        draft.items = draft.items ?? [];
                        draft.items[index] = draft.items[index] ?? {};
                        draft.items[index].tags = draft.items[index].tags ?? [];
                        draft.items[index].tags = uniq([
                          ...(draft.items[index].tags ?? []),
                          id,
                        ]);
                      }),
                    );
                  }
                }}
                sorted
              />
            </FormGroup>
          </ControlGroup>
        </FormGroup>
      ))}
    </>
  );
}

type ProfileLayoutEntryProps = {
  disabled?: boolean;
  profile?: ProfileModel;
  layout?: ProfileLayoutModel | null;
  onLayoutChanged: (position: ProfileLayoutModel) => void;
  onLayoutScreenRemoved: () => void;
};

function ProfileLayoutEntry({
  disabled,
  profile,
  layout,
  onLayoutChanged,
  onLayoutScreenRemoved,
}: ProfileLayoutEntryProps) {
  return (
    <div className="pl-5 border-l border-solid border-slate-200">
      {profile?.type === ScreenType.Custom && (
        <ControlGroup fill>
          <FormGroup disabled={disabled} label="Region (x)">
            <NumericInput
              min={0}
              disabled={disabled}
              allowNumericCharactersOnly
              fill
              value={layout?.position?.x ?? undefined}
              onValueChange={(value) => {
                onLayoutChanged({
                  ...layout,
                  position: {
                    ...layout?.position,
                    x: value,
                  },
                });
              }}
            />
          </FormGroup>
          <FormGroup className="ml-4" disabled={disabled} label="Region (y)">
            <NumericInput
              min={0}
              disabled={disabled}
              allowNumericCharactersOnly
              fill
              value={layout?.position?.y ?? undefined}
              onValueChange={(value) => {
                onLayoutChanged({
                  ...layout,
                  position: {
                    ...layout?.position,
                    y: value,
                  },
                });
              }}
            />
          </FormGroup>
          <FormGroup className="ml-4" disabled={disabled} label="Width">
            <NumericInput
              min={0}
              disabled={disabled}
              allowNumericCharactersOnly
              fill
              value={layout?.position?.width ?? undefined}
              onValueChange={(value) => {
                onLayoutChanged({
                  ...layout,
                  position: {
                    ...layout?.position,
                    width: value,
                  },
                });
              }}
            />
          </FormGroup>
          <FormGroup className="ml-4" disabled={disabled} label="Height">
            <NumericInput
              min={0}
              disabled={disabled}
              allowNumericCharactersOnly
              fill
              value={layout?.position?.height ?? undefined}
              onValueChange={(value) => {
                onLayoutChanged({
                  ...layout,
                  position: {
                    ...layout?.position,
                    height: value,
                  },
                });
              }}
            />
          </FormGroup>
        </ControlGroup>
      )}
      <FormGroup fill disabled={disabled} label="Layout">
        <ItemSelect
          activeItemId={layout?.layout}
          createDrawerElement={LayoutCreateDrawer}
          disabled={disabled}
          document={GetLayoutListNameIdDocument}
          onItemSelected={(id) =>
            onLayoutChanged({
              ...layout,
              layout: id,
            })
          }
        />
      </FormGroup>
      <FormGroup>
        <Switch
          disabled={disabled}
          label="Set interactive content at specific position"
          checked={!!layout?.flyout?.position}
          onChange={() =>
            onLayoutChanged({
              ...layout,
              flyout: {
                ...layout?.flyout,
                position: layout?.flyout?.position
                  ? undefined
                  : {
                      x: 0,
                      y: 0,
                      width: 0,
                      height: 0,
                    },
              },
            })
          }
        />
      </FormGroup>
      {layout?.flyout?.position && (
        <ControlGroup fill>
          <FormGroup disabled={disabled} label="Interative (x)">
            <NumericInput
              min={0}
              disabled={disabled}
              allowNumericCharactersOnly
              fill
              value={layout?.flyout?.position?.x ?? undefined}
              onValueChange={(value) => {
                onLayoutChanged({
                  ...layout,
                  flyout: {
                    ...layout?.flyout,
                    position: {
                      ...layout?.flyout?.position,
                      x: value,
                    },
                  },
                });
              }}
            />
          </FormGroup>
          <FormGroup
            className="ml-4"
            disabled={disabled}
            label="Interative (y)"
          >
            <NumericInput
              min={0}
              disabled={disabled}
              allowNumericCharactersOnly
              fill
              value={layout?.flyout?.position?.y ?? undefined}
              onValueChange={(value) => {
                onLayoutChanged({
                  ...layout,
                  flyout: {
                    ...layout?.flyout,
                    position: {
                      ...layout?.flyout?.position,
                      y: value,
                    },
                  },
                });
              }}
            />
          </FormGroup>
          <FormGroup className="ml-4" disabled={disabled} label="Width">
            <NumericInput
              min={0}
              disabled={disabled}
              allowNumericCharactersOnly
              fill
              value={layout?.flyout?.position?.width ?? undefined}
              onValueChange={(value) => {
                onLayoutChanged({
                  ...layout,
                  flyout: {
                    ...layout?.flyout,
                    position: {
                      ...layout?.flyout?.position,
                      width: value,
                    },
                  },
                });
              }}
            />
          </FormGroup>
          <FormGroup className="ml-4" disabled={disabled} label="Height">
            <NumericInput
              min={0}
              disabled={disabled}
              allowNumericCharactersOnly
              fill
              value={layout?.flyout?.position?.height ?? undefined}
              onValueChange={(value) => {
                onLayoutChanged({
                  ...layout,
                  flyout: {
                    ...layout?.flyout,
                    position: {
                      ...layout?.flyout?.position,
                      height: value,
                    },
                  },
                });
              }}
            />
          </FormGroup>
        </ControlGroup>
      )}
      <FormGroup>
        <Switch
          disabled={disabled}
          label="Assign content at specific layout positions"
          checked={!!layout?.detailed}
          onChange={() =>
            onLayoutChanged({
              ...layout,
              detailed: !layout?.detailed,
            })
          }
        />
      </FormGroup>
      {layout?.detailed ? (
        <ProfileLayoutPostTagMulti
          disabled={disabled}
          layout={layout}
          onLayoutChanged={onLayoutChanged}
        />
      ) : (
        <ProfileLayoutPostTagSingle
          disabled={disabled}
          layout={layout}
          onLayoutChanged={onLayoutChanged}
        />
      )}
      <div className="flex justify-end">
        {profile?.type === ScreenType.Custom && (
          <Button
            icon="remove"
            intent="danger"
            className="mt-4"
            minimal
            text="Remove Region"
            onClick={() => onLayoutScreenRemoved()}
          />
        )}
      </div>
    </div>
  );
}

type ProfileLayoutProps = {
  disabled?: boolean;
  profile?: ProfileModel;
  onProfileChanged: (
    callback: (draft: WritableDraft<ProfileModel>) => void,
  ) => void;
};

export function ProfileLayout({
  disabled,
  profile,
  onProfileChanged,
}: ProfileLayoutProps) {
  const [selectedTabId, setSelectedTabId] = useState<TabId>(`screen-${0}`);
  const tabs = useMemo(() => {
    if (profile?.type === ScreenType.Custom) {
      return profile?.layouts ?? [];
    }

    return Array.from({ length: profile?.screen?.count ?? 1 }).map(
      (_, index) => profile?.layouts?.[index] ?? { detailed: false },
    );
  }, [profile?.layouts, profile?.screen?.count, profile?.type]);
  const tabTitle = useMemo(() => {
    if (profile?.type === ScreenType.Custom) {
      return "Region";
    }
    return "Screen";
  }, [profile?.type]);

  return (
    <>
      <Tabs
        animate
        large
        selectedTabId={selectedTabId}
        onChange={(tabId) => setSelectedTabId(tabId)}
        vertical
      >
        {tabs.map((layout, index) => (
          <Tab
            key={index}
            className="outline-none w-full"
            disabled={disabled}
            id={`screen-${index}`}
            title={`${tabTitle} ${index + 1}`}
            panel={
              <ProfileLayoutEntry
                disabled={disabled}
                profile={profile}
                layout={layout}
                onLayoutChanged={(layout) => {
                  onProfileChanged((draft) => {
                    draft.layouts = draft.layouts ?? [];
                    draft.layouts[index] = layout;
                  });
                }}
                onLayoutScreenRemoved={() => {
                  onProfileChanged((draft) => {
                    draft.layouts = draft.layouts ?? [];
                    draft.layouts.splice(index, 1);
                  });
                }}
              />
            }
          />
        ))}
      </Tabs>
      {profile?.type === ScreenType.Custom && (
        <Button
          icon="add"
          intent="primary"
          className="mt-4"
          minimal
          text="Add Region"
          onClick={() => {
            onProfileChanged((draft) => {
              draft.layouts = draft.layouts ?? [];
              draft.layouts.push({
                detailed: false,
                position: {
                  x: 0,
                  y: 0,
                  width: 0,
                  height: 0,
                },
              });
            });
          }}
        />
      )}
    </>
  );
}
